import type { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { useBasket } from '../../../../bff-api/baskets/useBasket';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { sanitizeHtml } from '../../../../components/content-asset/sanitize-html';
import { GetIcon } from '../../../../components/icons';
import { Button, Text, Tooltip } from '../../../../ui-components';
import type { ProductItem } from '../../../../bff-api/baskets/model';

export const PromotionBadges: FC<ProductItem> = ({
    price,
    priceAfterItemDiscount,
    priceAfterOrderDiscount,
    priceAdjustments,
    c_isExcludedFromPromotion,
}) => {
    const { data: basket } = useBasket();
    const promotions = (basket?.orderPriceAdjustments || []).concat(priceAdjustments || []);

    if (c_isExcludedFromPromotion || !promotions.length) {
        return null;
    }

    // When a product is included in a promotion, the priceAfterItemDiscount or the priceAfterOrderDiscount is different than the price.
    if (price === priceAfterItemDiscount && price === priceAfterOrderDiscount) {
        return null;
    }

    return (
        <div className="flex flex-wrap gap-2" data-testid="cart-item-promotion-badges">
            {promotions.map(({ promotionId, itemText, c_details }) => (
                <div
                    key={promotionId}
                    className="flex items-center gap-1 rounded bg-slt-red-50 px-2 py-1"
                    data-testid={`cart-item-promotion-badge-${promotionId}`}
                >
                    <GetIcon name="tag" size="sm" />
                    <Text size="small" color="slt-red.1000">
                        <div
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                                __html: sanitizeHtml(itemText, {
                                    FORBID_ATTR: ['style'],
                                    FORBID_TAGS: ['strong'],
                                }),
                            }}
                        />
                    </Text>
                    <ConditionalRender visibility={!!c_details}>
                        <Tooltip label={c_details}>
                            <Button
                                id={`${promotionId}-details`}
                                variant="text-button-no-hover"
                                color="slt-red.1000"
                                textSize="small"
                                display="flex"
                            >
                                <FormattedMessage
                                    id="promo-code.badge.details"
                                    defaultMessage="Details"
                                />
                            </Button>
                        </Tooltip>
                    </ConditionalRender>
                    <Text size="small" color="slt-red.1000" style="bold">
                        <FormattedMessage id="promo-code.badge.applied" defaultMessage="Applied" />
                    </Text>
                </div>
            ))}
        </div>
    );
};
